import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { classNames } from "../../utils/cn";
import { Spinner } from "../icons/Spinner";
import { withMarginTw } from "../../styles/withMargin";
import { Tooltip } from "../Tooltip";
import { KeyboardShortcut } from "../keyboard_shortcut";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

export type ButtonVariant =
  | "neutral"
  | "neutral/inverted"
  | "neutral/soft"
  | "positive"
  | "positive/inverted"
  | "secondary"
  | "danger"
  | "green";

export type ButtonMenuItem = {
  label: string;
  onClick: () => void;
  icon?: React.ElementType;
  isDisabled?: boolean;
  isLoading?: boolean;
};

interface Props {
  variant: ButtonVariant;
  text: string;
  onClick?: () => void;
  id?: string;
  icon?: React.ElementType;
  iconColor?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  loadingText?: string;
  type?: "submit" | "reset" | "button";
  margin?: string;
  keyboardShortcut?: string;
  size?: "xs" | "s" | "m";
  tooltip?: string;
  badge?: number;
  trail?: {
    type: "spinner" | "badge" | "icon" | "tooltip" | "shortcut";
    icon?: React.ElementType;
    badge?: number;
    tooltip?: string;
    shortcut?: string;
  };
  menu?: {
    items: ButtonMenuItem[];
    label?: string;
  };
}

const badgeColors: Record<ButtonVariant, string> = {
  neutral: "bg-gray-200 text-gray-600",
  "neutral/inverted": "bg-gray-600 text-white",
  "neutral/soft": "bg-concrete-200 text-concrete-600",
  positive: "bg-persian-200 text-persian-800",
  danger: "bg-red-200 text-red-800",
  green: "bg-green-200 text-green-800",
  "positive/inverted": "bg-persian-200 text-persian-800",
  secondary: "bg-persian-400 text-persian-800",
};

function variantClasses(variant: ButtonVariant) {
  return classNames(
    variant === "neutral"
      ? "bg-white text-gray-600 ring-gray-300 hover:bg-gray-50 focus-visible:outline-gray-600 active:bg-gray-100"
      : "",
    variant === "neutral/soft"
      ? "ring-concrete-100 bg-white text-concrete-950 hover:bg-concrete-50 active:bg-concrete-100"
      : "",
    variant === "positive"
      ? "bg-persian-800 text-white ring-persian-300 hover:bg-persian-700/90 focus-visible:outline-persian-800"
      : "",
    variant === "secondary"
      ? "bg-persian-200 text-persian-990 ring-persian-200 hover:bg-persian-100 focus-visible:outline-persian-300 active:bg-persian-300"
      : "",
    variant === "positive/inverted"
      ? "bg-persian-50 text-persian-800 ring-persian-800 hover:bg-persian-100 focus-visible:outline-persian-800"
      : "",
    variant === "danger"
      ? "bg-red-600 ring-red-500 text-white hover:bg-red-700 focus-visible:outline-red-800"
      : "",
    variant === "green"
      ? "bg-green-600 ring-green-500 text-white hover:bg-green-500 focus-visible:outline-green-600"
      : "",
    variant === "neutral/inverted"
      ? "bg-gray-600 text-white ring-gray-300 hover:bg-gray-800 focus-visible:outline-gray-600"
      : "",
  );
}

function sizeClasses(size?: "xs" | "s" | "m") {
  if (size === "xs") return "text-xs px-1.5 py-1";
  if (size === "s") return "text-sm px-2 py-1.5";
  return "px-3.5 py-2.5";
}

function menuTriggerSizeClasses(size?: "xs" | "s" | "m") {
  if (size === "xs") return "px-1 py-1";
  if (size === "s") return "px-1.5 py-1.5";
  return "px-2 py-2.5";
}

function iconSizeClass(size?: "xs" | "s" | "m") {
  if (size === "xs") return "w-3 h-3";
  if (size === "s") return "w-4 h-4";
  return "w-5 h-5";
}

function ButtonContent(props: Props) {
  const { text, icon: Icon, isLoading, loadingText, size, variant, trail } =
    props;

  return (
    <>
      {isLoading && loadingText ? loadingText : text}
      {isLoading ? (
        <div className={iconSizeClass(size)}>
          <Spinner
            size={size === "xs" || size === "s" ? "s" : "m"}
            color={variant === "neutral" ? "gray" : "white"}
            withMargin={false}
          />
        </div>
      ) : Icon ? (
        <Icon
          className={clsx(iconSizeClass(size), `${props.iconColor}`)}
        />
      ) : null}
      {trail ? <Trail {...props} /> : null}
    </>
  );
}

// Update your component to use forwardRef
export const Button = React.forwardRef<HTMLButtonElement, Props>(
  (props, ref) => {
    const {
      variant,
      onClick,
      id,
      isLoading,
      isDisabled,
      type,
      margin,
      size,
      menu,
    } = props;
    const cacledMargin = withMarginTw({ margin });
    const actor = useSelector(authSelectors.actor);
    const btnDisabled = isDisabled || actor !== undefined;
    const hasMenu = Boolean(menu && menu.items.length > 0);

    const sharedButtonClasses = classNames(
      btnDisabled ? "cursor-not-allowed opacity-70" : "",
      "relative text-center truncate inline-flex justify-center items-center gap-x-2 text-sm font-semibold ring-1 ring-inset focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
      variantClasses(variant),
      isLoading ? "cursor-not-allowed" : "",
    );

    const handleMainClick = () => {
      if (btnDisabled || isLoading) {
        return;
      }
      onClick?.();
    };

    if (!hasMenu) {
      return (
        <button
          id={id}
          ref={ref}
          type={type}
          className={classNames(
            cacledMargin,
            sharedButtonClasses,
            "rounded-md shadow-sm",
            sizeClasses(size),
          )}
          onClick={handleMainClick}
          disabled={isLoading}
        >
          <ButtonContent {...props} />
        </button>
      );
    }

    return (
      <Menu as="div" className={classNames(cacledMargin, "relative z-30 inline-block")}>
        {({ open }) => (
          <span
            className={classNames(
              "inline-flex items-stretch rounded-md shadow-sm isolate",
              open ? "z-30" : "",
            )}
          >
            <button
              id={id}
              ref={ref}
              type={type}
              className={classNames(
                sharedButtonClasses,
                "rounded-l-md rounded-r-none focus:z-10",
                sizeClasses(size),
              )}
              onClick={handleMainClick}
              disabled={isLoading}
            >
              <ButtonContent {...props} />
            </button>
            <div className="relative -ml-px flex self-stretch">
              <Menu.Button
                type="button"
                disabled={btnDisabled}
                className={classNames(
                  sharedButtonClasses,
                  "rounded-r-md rounded-l-none focus:z-10 h-full",
                  menuTriggerSizeClasses(size),
                )}
              >
                <span className="sr-only">{menu?.label ?? "More options"}</span>
                <ChevronDownIcon
                  className={iconSizeClass(size)}
                  aria-hidden="true"
                />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 top-full z-50 mt-1 min-w-[14rem] origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {menu!.items.map((item) => (
                    <Menu.Item
                      key={item.label}
                      disabled={item.isDisabled || item.isLoading}
                    >
                      {({ active }) => (
                        <button
                          type="button"
                          disabled={item.isDisabled || item.isLoading}
                          onClick={item.onClick}
                          className={classNames(
                            active ? "bg-gray-50" : "bg-white",
                            "flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm text-gray-700 disabled:opacity-60",
                          )}
                        >
                          {item.isLoading ? (
                            <Spinner size="s" color="gray" withMargin={false} />
                          ) : item.icon ? (
                            <item.icon className="h-4 w-4 text-gray-500" />
                          ) : null}
                          {item.label}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </div>
          </span>
        )}
      </Menu>
    );
  },
);

function Trail(props: Props) {
  if (!props.trail) {
    return null;
  }

  if (props.trail.type === "spinner") {
    return (
      <div
        className={
          props.size === "xs"
            ? "w-3 h-3"
            : props.size === "s"
              ? "w-4 h-4"
              : "w-5 h-5"
        }
      >
        <Spinner
          size={props.size === "xs" ? "s" : props.size === "s" ? "s" : "m"}
          color={props.variant === "neutral" ? "gray" : "white"}
        />
      </div>
    );
  }

  if (props.trail.type === "badge") {
    return (
      <span
        className={classNames(
          "inline-flex items-center justify-center w-4 h-4 text-xs font-semibold rounded-full",
          badgeColors[props.variant],
        )}
      >
        {props.trail.badge}
      </span>
    );
  }

  if (props.trail.type === "icon" && props.trail.icon) {
    return (
      <props.trail.icon
        className={
          props.size === "xs"
            ? "w-3 h-3"
            : props.size === "s"
              ? "w-4 h-4"
              : "w-5 h-5"
        }
      />
    );
  }

  if (props.trail.type === "tooltip" && props.trail.tooltip) {
    return <Tooltip text={props.trail.tooltip} />;
  }

  if (props.trail.type === "shortcut" && props.trail.shortcut) {
    return (
      <KeyboardShortcut
        shortcut={props.trail.shortcut}
        variant={props.variant}
      />
    );
  }

  return null;
}

Button.displayName = "Button";

export function ButtonPopout(props: {
  open: boolean;
  onClose?: () => void;
  buttonElementId: string;
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "left" | "right";
  offset?: number;
  className?: string;
}) {
  const {
    open,
    onClose,
    buttonElementId,
    children,
    align = "end",
    side = "bottom",
    offset = 8,
    className,
  } = props;

  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const [maxWidth, setMaxWidth] = React.useState<number | undefined>(undefined);
  const [animatingOut, setAnimatingOut] = React.useState(false);

  const updatePosition = React.useCallback(() => {
    const el = document.getElementById(buttonElementId);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let top = rect.bottom + offset;
    let left = rect.left;

    if (side === "top") top = rect.top - offset;
    if (side === "left") {
      left = rect.left - offset;
      top = rect.top;
    }
    if (side === "right") {
      left = rect.right + offset;
      top = rect.top;
    }

    if (align === "center") {
      left = rect.left + rect.width / 2;
    } else if (align === "end") {
      left = rect.right;
    }

    setPosition({ top: top + window.scrollY, left: left + window.scrollX });
    setMaxWidth(rect.width);
  }, [buttonElementId, align, side, offset]);

  React.useEffect(() => {
    if (!open) return;
    updatePosition();
    const handle = () => updatePosition();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, { passive: true });
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle as any);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (onClose) onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const backdropRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open && animatingOut) {
      const t = setTimeout(() => setAnimatingOut(false), 150);
      return () => clearTimeout(t);
    }
  }, [open, animatingOut]);

  const originClass = React.useMemo(() => {
    if (side === "bottom") {
      if (align === "end") return "origin-top-right";
      if (align === "center") return "origin-top";
      return "origin-top-left";
    }
    if (side === "top") {
      if (align === "end") return "origin-bottom-right";
      if (align === "center") return "origin-bottom";
      return "origin-bottom-left";
    }
    if (side === "left") return "origin-top-right";
    if (side === "right") return "origin-top-left";
    return "origin-top-left";
  }, [side, align]);

  if (!open && !animatingOut) return null;

  const show = open;
  const stateClass = show ? "data-[state=open]" : "data-[state=closed]";

  const node = (
    <div className="fixed inset-0 z-[60]">
      {/* backdrop for outside-click; keep pointer-events on */}
      <div
        ref={backdropRef}
        className={clsx(
          "absolute inset-0",
          show ? "pointer-events-auto" : "pointer-events-none",
        )}
        onMouseDown={(e) => {
          const target = e.target as Node;
          if (contentRef.current && !contentRef.current.contains(target)) {
            if (onClose) onClose();
          }
        }}
      />

      {/* content */}
      <div
        ref={contentRef}
        className={clsx(
          "absolute pointer-events-auto will-change-transform",
          originClass,
          "transition duration-150",
          stateClass,
          show ? "opacity-100" : "opacity-0",
          show ? "scale-100" : "scale-95",
          "bg-white border border-gray-200 rounded-md shadow-lg",
          className,
        )}
        style={{
          top: position ? position.top : 0,
          left: position ? position.left : 0,
          transform:
            align === "center"
              ? "translate(-50%, 0)"
              : align === "end"
                ? "translate(-100%, 0)"
                : "translate(0, 0)",
        }}
        onAnimationEnd={() => {
          if (!show) setAnimatingOut(false);
        }}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

(Button as any).Popout = ButtonPopout;
